import type { SupabaseClient } from '@supabase/supabase-js'

function parseHashParams(): URLSearchParams {
  const raw = window.location.hash.replace(/^#/, '')
  return raw ? new URLSearchParams(raw) : new URLSearchParams()
}

function authCallbackType(): string | null {
  const fromHash = parseHashParams().get('type')
  if (fromHash) return fromHash
  return null
}

/** Strip tokens from the URL after session is established. */
export function cleanAuthCallbackFromUrl() {
  window.history.replaceState({}, '', window.location.pathname)
}

/**
 * True when the current URL looks like a Supabase email auth callback
 * (recovery, invite, PKCE code exchange, or a GoTrue /verify error redirect
 * for one of those flows, e.g. an expired or already-used invite link).
 */
export function isAuthCallbackUrl(): boolean {
  const search = new URLSearchParams(window.location.search)
  if (search.has('code')) return true
  const hash = parseHashParams()
  if (hash.has('access_token')) return true
  const type = hash.get('type')
  if (type === 'recovery' || type === 'invite' || type === 'signup') return true
  // GoTrue's /verify redirects failed recovery/invite links here too (e.g.
  // "email link has expired"), with no access_token/type - only error info.
  return hash.has('error') || hash.has('error_code')
}

/**
 * If Supabase redirected to Site URL (/) with recovery/invite tokens, send user to
 * /reset-password before React Router mounts protected routes.
 */
export function maybeRedirectAuthCallbackToResetPassword() {
  if (typeof window === 'undefined') return
  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  if (path === '/reset-password') return
  if (!isAuthCallbackUrl()) return
  const type = authCallbackType()
  const search = window.location.search
  const hash = window.location.hash
  const isPasswordFlow =
    type === 'recovery' ||
    type === 'invite' ||
    search.includes('code=') ||
    hash.includes('access_token') ||
    hash.includes('error=') ||
    hash.includes('error_code=')
  if (!isPasswordFlow) return
  window.location.replace(`/reset-password${search}${hash}`)
}

/**
 * Establish a Supabase session from ?code= (PKCE) or #access_token= (implicit) in the URL.
 */
export async function establishAuthSessionFromUrl(
  client: SupabaseClient,
): Promise<{ ok: boolean; error?: string }> {
  if (isDeadAuthLinkUrl()) {
    const desc = decodeHashErrorDescription(parseHashParams().get('error_description'))
    return { ok: false, error: desc || 'Email link is invalid or has expired' }
  }

  const searchParams = new URLSearchParams(window.location.search)
  const code = searchParams.get('code')

  if (code) {
    const { error } = await client.auth.exchangeCodeForSession(code)
    if (error) return { ok: false, error: error.message }
    cleanAuthCallbackFromUrl()
    return { ok: true }
  }

  const hashParams = parseHashParams()
  const accessToken = hashParams.get('access_token')
  const refreshToken = hashParams.get('refresh_token')

  if (accessToken && refreshToken) {
    const { error } = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    })
    if (error) return { ok: false, error: error.message }
    cleanAuthCallbackFromUrl()
    return { ok: true }
  }

  const {
    data: { session },
  } = await client.auth.getSession()
  if (session) return { ok: true }

  // GoTrue redirects failed /verify attempts (expired or already-used invite
  // or recovery links) here with error info instead of tokens.
  const errorDescription = hashParams.get('error_description')
  if (errorDescription) {
    return { ok: false, error: decodeURIComponent(errorDescription.replace(/\+/g, ' ')) }
  }

  return {
    ok: false,
    error:
      'Auth session missing! Use the link from your invite email in one click. If it expired, request a new invite from this page.',
  }
}

/**
 * GoTrue error copy for expired, already-used, or otherwise dead invite/recovery links.
 */
export function isDeadAuthLinkMessage(message: string | null | undefined): boolean {
  if (!message) return false
  const m = message.toLowerCase()
  return (
    m.includes('expired') ||
    m.includes('otp') ||
    m.includes('one-time token') ||
    m.includes('invalid') ||
    m.includes('auth session missing')
  )
}

function decodeHashErrorDescription(raw: string | null): string | null {
  if (!raw) return null
  return decodeURIComponent(raw.replace(/\+/g, ' '))
}

/**
 * True when this page load is a failed GoTrue /verify redirect (expired,
 * already-used, or invalid invite/recovery link). A leftover session from an
 * earlier click must not win over this - the link the user just opened is dead.
 */
export function isDeadAuthLinkUrl(): boolean {
  if (typeof window === 'undefined') return false
  const hash = parseHashParams()
  if (hash.get('error_code') === 'otp_expired') return true
  if (!hash.has('error') && !hash.has('error_code')) return false
  const desc = decodeHashErrorDescription(hash.get('error_description'))
  if (desc) return isDeadAuthLinkMessage(desc)
  return true
}
